import type { Locale } from '@tvitvi/shared';
import * as RadixPopover from '@radix-ui/react-popover';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ICONS } from '@/components/ui/icons';
import { stopPortalClick } from '@/components/ui/portal-events';
import { INPUT_CLASS } from '@/components/ui/TextField';
import { usePortalTheme } from '@/components/ui/use-portal-theme';
import { intlTag } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * A date, typed or picked, drawn by Tvitvi rather than by the operating system.
 *
 * WHY THIS FILE EXISTS AT ALL. DESIGN.md §13 is unconditional — "Ingen native velgere.
 * Noensinne." — and `no-native-pickers.spec.ts` fails on `<input type="date">` anywhere under
 * `src`. `Select` and `TimeField` are the two worked examples of the alternative; there was no
 * date equivalent, and a deadline is the first field in the product that needs one. So it is
 * built the same way: behaviour from Radix, surface ours, and the colour mode carried across the
 * portal by `usePortalTheme`, because a portalled panel sits outside every `[data-theme]`
 * subtree on the page.
 *
 * WHERE IT SHOULD LIVE. `components/ui`, beside `TimeField`, which is this control one unit up.
 * It is here because this page was built as one of several parallel agents whose file ownership
 * stops at `features/tasks/**`. Promoting it is a move and an import rewrite; it should happen
 * before a second page needs a date.
 *
 * IT OWNS NO PAGE COPY. Every string it shows comes from `Intl` in the reader's locale — month
 * names, weekday initials, the full date a screen reader hears — or from the two labels the
 * caller hands it. That is what makes it safe to move: it carries no `tasks.*` key with it, and
 * the page that uses it is the page that names its buttons.
 *
 * The one exception is `date.chooseMonth`, the accessible name of the header button that opens
 * the month/year picker (#17b). `Intl` can name the months but not the act of choosing one, and
 * a third caller-supplied label for a control every caller gets identically would have been a
 * prop nobody could pass differently. It lives under `date.*`, beside `time.*`, which `TimeField`
 * has needed for the same reason since it was written. `date.chooseYear` and `date.placeholder`
 * («dd.mm.åååå») joined it in bølge 0 for the same reason.
 *
 * THE VALUE IS A CALENDAR DATE, NOT AN INSTANT. `YYYY-MM-DD`, with no timezone anywhere in this
 * component. A picker that reasons in UTC shows the wrong day for half of every evening in Oslo.
 * Turning the date into the timestamp `tasks.dueAt` wants is the form's job, done once, in one
 * place, where the convention can be stated out loud.
 *
 * TYPING IS THE FAST PATH, exactly as in `TimeField`. `27082026` and `27.08.2026` are the same
 * day, `2708` is that day this year, and `27` is that day this month — the ways a production
 * office writes a date on a whiteboard. The panel is for choosing, not for entering, which is
 * why it never takes the focus off the input when it opens.
 *
 * DAY FIRST, BOTH LANGUAGES. `nb-NO` and `en-GB` are the only locales this product has and both
 * are day-first, so the parse is unambiguous and the display is whatever `Intl` renders.
 */

const WEEK_LENGTH = 7;
/** Six rows always: a month spans five or six weeks, and a grid that changes height jumps. */
const WEEKS_SHOWN = 6;

/** Monday. Both supported locales start their week there, and a calendar is read against a wall. */
const FIRST_WEEKDAY = 1;

function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

/** `YYYY-MM-DD` for a local calendar date. Never `toISOString`, which would shift the day. */
export function toDateValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The local `Date` a `YYYY-MM-DD` names, or null. Rejects 31 February rather than rolling it. */
export function parseDateValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/**
 * What somebody meant, applied while they type and again when they leave the field.
 *
 * Day, then month, then year. A two-digit year is read as this century: a deadline is never in
 * 1926, and asking for four digits for a festival eight weeks away is two keystrokes that carry
 * no information.
 */
export function parseTypedDate(text: string, today: Date): string | null {
  const digits = text.replace(/\D/g, '');
  if (digits.length === 0) return null;

  let day: number;
  let month: number;
  let year: number;

  if (digits.length <= 2) {
    day = Number(digits);
    month = today.getMonth() + 1;
    year = today.getFullYear();
  } else if (digits.length === 4) {
    day = Number(digits.slice(0, 2));
    month = Number(digits.slice(2, 4));
    year = today.getFullYear();
  } else if (digits.length === 6) {
    day = Number(digits.slice(0, 2));
    month = Number(digits.slice(2, 4));
    year = 2000 + Number(digits.slice(4, 6));
  } else if (digits.length === 8) {
    day = Number(digits.slice(0, 2));
    month = Number(digits.slice(2, 4));
    year = Number(digits.slice(4, 8));
  } else {
    return null;
  }

  const candidate = new Date(year, month - 1, day);
  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null;
  }
  return toDateValue(candidate);
}

/** The reader's own numeric date: `27.08.2026` in Norwegian, `27/08/2026` in English. */
export function displayDate(value: string, locale: Locale): string {
  const date = parseDateValue(value);
  if (date === null) return value;
  return new Intl.DateTimeFormat(intlTag(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function addMonths(date: Date, months: number): Date {
  // Clamped to the last day of the target month: 31 January plus one month is 28 February, not
  // 3 March. A picker that skips a month when you page through it is a picker nobody trusts.
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), lastDay));
}

/** The 42 days a month grid shows, starting on the Monday on or before the first of the month. */
function gridFor(month: Date): readonly Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() - FIRST_WEEKDAY + WEEK_LENGTH) % WEEK_LENGTH;
  const start = addDays(first, -offset);
  return Array.from({ length: WEEKS_SHOWN * WEEK_LENGTH }, (_, index) => addDays(start, index));
}

/** Monday…Sunday, short, in the reader's language. Built from a known week, never hardcoded. */
function weekdayLabels(locale: Locale): readonly string[] {
  const formatter = new Intl.DateTimeFormat(intlTag(locale), { weekday: 'short' });
  // 1 January 2024 was a Monday. Any Monday would do; a constant one keeps this pure.
  const monday = new Date(2024, 0, 1);
  return Array.from({ length: WEEK_LENGTH }, (_, index) =>
    formatter.format(addDays(monday, index)),
  );
}

function monthLabel(month: Date, locale: Locale): string {
  return new Intl.DateTimeFormat(intlTag(locale), { month: 'long', year: 'numeric' }).format(month);
}

function longDate(date: Date, locale: Locale): string {
  return new Intl.DateTimeFormat(intlTag(locale), { dateStyle: 'full' }).format(date);
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export interface DateFieldProps {
  /** `YYYY-MM-DD`, or `''` for no date. Controlled: this component never owns the value. */
  value: string;
  onValueChange: (value: string) => void;
  locale: Locale;
  /** The panel's two shortcuts, named by the page — see "it owns no copy" above. */
  todayLabel: string;
  clearLabel: string;
  /** Injected so "today" is a value a test can pin, not a property of the machine. */
  today?: Date;
  /**
   * HVILKEN MÅNED KALENDEREN ÅPNER PÅ NÅR FELTET ER TOMT. `YYYY-MM-DD`.
   *
   * Atle, 2. september: «Flytt med det auto-måneden du åpner kalendern i, slik at man 2 ganger
   * slipper å bla alle mnd frem i tid.» A festival is planned nine months out, so an empty date
   * field that opens on the current month opens nine pages away from every date anybody is about
   * to type — and it does it again for the second field of the same pair.
   *
   * The caller passes what it knows: the departure for an arrival, the check-in for a check-out,
   * the årgang's own dates for the first field of all. It moves only the OPENING month — the
   * field stays empty, nothing is preselected, and «I dag» still means today.
   */
  anchor?: string;
  /**
   * DEN TIDLIGSTE DAGEN SOM KAN VELGES, `YYYY-MM-DD` (QA6 K5, L1). Dagene før er sperret i
   * kalenderen. Et utløp i fortiden er en skrivefeil og ikke en deling — velgeren skal ikke tilby
   * den. En skrevet dato sjekkes fortsatt av skjemaet.
   */
  min?: string;
  /** Påkrevd felt: «Fjern datoen» tegnes ikke, fordi et tomt felt ikke er et gyldig svar. */
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  id?: string;
  className?: string;
  /**
   * For et felt UTEN `Field` rundt seg (massehandlingslinja, K6 L5): navnet og teksten i det tomme
   * feltet. I en `Field` bærer etiketten begge, og da utelates de.
   */
  placeholder?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'data-testid'?: string;
  ref?: Ref<HTMLInputElement>;
}

export function DateField({
  value,
  onValueChange,
  locale,
  todayLabel,
  clearLabel,
  today = new Date(),
  anchor,
  min,
  required = false,
  disabled = false,
  readOnly = false,
  name,
  id,
  className,
  placeholder,
  'aria-label': ariaLabel,
  ref,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'data-testid': testId,
}: DateFieldProps): ReactNode {
  const { t } = useTranslation();
  const portal = usePortalTheme();
  const Calendar = ICONS.booking;

  const [open, setOpen] = useState(false);
  /** What the user is typing, while they are typing it. `null` means "show the value". */
  const [draft, setDraft] = useState<string | null>(null);

  const gridId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  /**
   * HELE PANELET, IKKE BARE DAGRUTENETTET — og det er rettelsen av #17b.
   *
   * `onBlur` under holdt panelet åpent bare når fokus gikk inn i `gridRef`, som er de 42
   * dagcellene. Månedspilene ‹ › står UTENFOR det rutenettet, i raden over. Et klikk på en pil
   * flyttet derfor fokus ut av «velgeren» slik feltet så den, panelet lukket seg på blur, og
   * knappen var borte fra DOM-en før klikket rakk å lande. Målt i jsdom 19. september: etter ett
   * klikk på ›, ingen `[data-testid="date-panel"]` i treet i det hele tatt.
   *
   * Det er derfor pilene «ikke bytter måned»: de blir aldri trykket. Samme felle gjelder hver
   * kontroll panelet får senere — «I dag», «Tøm», hurtigvelgeren — så grensen flyttes til
   * panelet selv framfor å nevne dem én etter én.
   */
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** Set only on the path that finishes a date, so a dismissal keeps Radix's own restore. */
  const returnToInput = useRef(false);
  /**
   * THE ONE FOCUS EVENT THAT MUST NOT OPEN THE PANEL.
   *
   * Choosing a day closes the panel and puts the focus back in the input — and focus in the
   * input is what opens the panel. Without this the calendar reopened the instant a day was
   * picked, over the form, every time. The unit test could not see it (jsdom fires the events
   * but nothing is on screen to be covered) and Playwright caught it on the first run: the panel
   * was still `data-state="open"` after the click.
   *
   * A ref rather than state, and cleared by the very handler it suppresses, so it can only ever
   * swallow the ONE focus that the pick itself caused.
   */
  const skipNextOpen = useRef(false);

  const selected = parseDateValue(value);
  const text = draft ?? (value === '' ? '' : displayDate(value, locale));

  function setPanel(next: boolean): void {
    if (next && (disabled || readOnly)) return;
    if (next) portal.capture();
    setOpen(next);
  }

  /** Close, and hand the caret back to the field the reader was filling in. */
  function finish(): void {
    returnToInput.current = true;
    skipNextOpen.current = true;
    setPanel(false);
  }

  function commit(next: string): void {
    setDraft(null);
    onValueChange(next);
  }

  return (
    <RadixPopover.Root open={open} onOpenChange={setPanel}>
      {/*
        The INPUT is the anchor, and there is no trigger button — the same shape `SuggestField`
        uses. A separate button would need a name of its own, and the only honest one is the
        field's own label, which the input already carries. Focusing the field opens the panel;
        the calendar glyph is decoration that says which kind of field this is, and it does not
        take the pointer (`pointer-events-none`), so a click on it lands on the input.
      */}
      <RadixPopover.Anchor asChild>
        <input
          id={id}
          name={name}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? gridId : undefined}
          aria-haspopup="grid"
          inputMode="numeric"
          autoComplete="off"
          maxLength={10}
          disabled={disabled}
          readOnly={readOnly}
          /* Formen man kan skrive, der feltet tar skriving (bølge 0, A4). I en `Field` skjules den
             til navnet har flyttet seg opp: se `.field-control::placeholder` i globals.css. */
          placeholder={placeholder ?? (readOnly || disabled ? undefined : t('date.placeholder'))}
          aria-label={ariaLabel}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          data-testid={testId}
          value={text}
          ref={(node) => {
            inputRef.current = node;
            portal.anchorRef(node);
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            // Eight digits is the only length that can mean exactly one thing, so it commits
            // as it is typed. Anything shorter waits for the blur, or a half-typed `27` would
            // become the 27th of this month on the way to 27.08.2027.
            if (next.replace(/\D/g, '').length === 8) {
              const parsed = parseTypedDate(next, today);
              if (parsed !== null) commit(parsed);
            }
          }}
          onFocus={() => {
            if (skipNextOpen.current) {
              skipNextOpen.current = false;
              return;
            }
            setPanel(true);
          }}
          onBlur={(event) => {
            /*
             * PANELET LUKKER SEG NÅR FOKUS FORLATER FELTET (QA-punkt 23).
             *
             * Rapporten: «Kalender-popupen blir stående åpen når fokus flyttes eller man klikker
             * utenfor. Den dekker da Send-knappen og lukkes bare med Esc.»
             *
             * Panelet åpnes av FOKUS i inputen og tar ikke fokus selv — det er hele grunnen til at
             * tastaturveien (pil ned) virker. Prisen er at Radix sitt eget «klikk utenfor» aldri
             * får noe å lukke: laget har ingen fokus å miste.
             *
             * `relatedTarget` er hvor fokus GIKK. Gikk det inn i PANELET — rutenettet, pilene,
             * hurtigvelgeren, «I dag» — er vi fortsatt i velgeren og panelet blir stående; gikk
             * det noe annet sted, er man ferdig med feltet. Null — et klikk på bakgrunnen —
             * teller som ferdig. Se `panelRef` for hvorfor grensen ikke lenger er rutenettet.
             */
            const into = event.relatedTarget;
            const stayed = into instanceof Node && panelRef.current?.contains(into) === true;
            if (!stayed) setPanel(false);

            if (draft === null) return;
            if (draft.trim() === '') commit('');
            else {
              const parsed = parseTypedDate(draft, today);
              // Unparseable text goes back to the value it had: a half-typed date is never
              // silently turned into a different one.
              if (parsed !== null) commit(parsed);
              else setDraft(null);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              // Into the grid. This is the keyboard path to the picker, and the reason the
              // panel does not steal focus when it opens.
              event.preventDefault();
              setPanel(true);
              queueMicrotask(() => {
                gridRef.current
                  ?.querySelector<HTMLButtonElement>('[data-focus-target="true"]')
                  ?.focus();
              });
              return;
            }
            if (event.key === 'Escape' && open) {
              // Keeps what was typed. `stopPropagation` because this field is used inside a
              // dialog, and one Escape must close one layer.
              event.preventDefault();
              event.stopPropagation();
              setPanel(false);
            }
          }}
          className={cn(INPUT_CLASS, !readOnly && 'pe-9', 'numeric', className)}
        />
      </RadixPopover.Anchor>
      {readOnly ? null : (
        <Calendar
          aria-hidden
          className="pointer-events-none absolute inset-y-0 end-4 my-auto size-3.5 text-muted"
        />
      )}

      <RadixPopover.Portal>
        <RadixPopover.Content
          // Stamped with the colour mode of the pane the input lives in, because this panel is
          // portalled out of it. See `usePortalTheme`.
          {...portal.attributes}
          onClick={stopPortalClick}
          // Focus in the panel is still focus in the field; `Field` reads this to keep the
          // floating label up while somebody is choosing.
          data-field-surface={id}
          data-testid="date-panel"
          ref={panelRef}
          /*
            KALENDEREN ÅPNER NEDOVER, ALLTID (QA13 N18, 20. september).

            «Kalenderpopoveren åpner oppover og dekker Navn- og År-feltene i dialogen.» Det var
            Radix som gjorde jobben sin: uten plass under vendte den panelet opp. I en dialog er
            det feil vei uansett hvor lite plass det er — det som ligger over feltet er skjemaet
            man holder på å fylle ut, og et panel som skjuler det man nettopp skrev er verre enn et
            panel man må rulle i.

            Så: ingen vending, og en høyde bundet til plassen som faktisk finnes under. Er den for
            liten, ruller kalenderen i stedet for å legge seg over navnet.
          */
          side="bottom"
          avoidCollisions={false}
          align="end"
          sideOffset={4}
          collisionPadding={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => {
            if (!returnToInput.current) return;
            returnToInput.current = false;
            event.preventDefault();
            inputRef.current?.focus();
          }}
          // The input is OUTSIDE this panel, so every click in the field reads as a click
          // outside it — and the panel would close the moment somebody moved the caret.
          onInteractOutside={(event) => {
            const target = event.target;
            if (target instanceof Node && inputRef.current?.contains(target) === true) {
              event.preventDefault();
            }
          }}
          className={cn(
            'glass-popover z-50 overflow-y-auto',
            'max-h-[var(--radix-popover-content-available-height)]',
            'text-default',
          )}
        >
          <MonthGrid
            gridRef={gridRef}
            gridId={gridId}
            selected={selected}
            today={today}
            anchor={anchor === undefined ? null : parseDateValue(anchor)}
            min={min ?? null}
            clearable={!required}
            locale={locale}
            todayLabel={todayLabel}
            clearLabel={clearLabel}
            onPick={(date) => {
              commit(toDateValue(date));
              finish();
            }}
            onClear={() => {
              commit('');
              finish();
            }}
            onDismiss={finish}
          />
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

interface MonthGridProps {
  gridRef: { current: HTMLDivElement | null };
  gridId: string;
  selected: Date | null;
  today: Date;
  /** Where to open when nothing is selected — see `DateFieldProps.anchor`. */
  anchor: Date | null;
  /** Se `DateFieldProps.min`. `YYYY-MM-DD`, sammenlignet som tekst. */
  min: string | null;
  clearable: boolean;
  locale: Locale;
  todayLabel: string;
  clearLabel: string;
  onPick: (date: Date) => void;
  onClear: () => void;
  onDismiss: () => void;
}

/**
 * One month.
 *
 * A roving tabindex over real buttons rather than `aria-activedescendant`: real DOM focus is what
 * makes a day a target for a finger as well as for a screen reader, and what lets the arrow keys
 * cross a month boundary without the caller tracking an index. One tab stop for the whole grid.
 *
 * The month-navigation buttons are named with the month they lead to — "juli 2026" — rather than
 * with a translated "forrige måned". It is a better name than either would be (it says where you
 * land, not which way you go), and it needs no key: `Intl` already speaks the reader's language.
 */
function MonthGrid({
  gridRef,
  gridId,
  selected,
  today,
  anchor,
  min,
  clearable,
  locale,
  todayLabel,
  clearLabel,
  onPick,
  onClear,
  onDismiss,
}: MonthGridProps): ReactNode {
  const { t } = useTranslation();
  /*
   * ÅPNINGSMÅNEDEN, OG #17d.
   *
   * `selected ?? anchor ?? today`, i den rekkefølgen, og bare ved montering. Er feltet satt til
   * 18.06.2027 åpner rutenettet i juni 2027 — ikke i inneværende år, som var det Atle så. Er det
   * tomt, følger det ankeret kalleren ga (søsterfeltets dato, årgangens egne datoer), og først
   * til slutt dagens dato.
   */
  const [month, setMonth] = useState<Date>(() => selected ?? anchor ?? today);
  /** Hurtigvelgeren for måned og år. Den ERSTATTER rutenettet — aldri et lag oppå det (J1). */
  const [quick, setQuick] = useState(false);
  const Previous = ICONS.chevronLeft;
  const Next = ICONS.chevronRight;
  const Expand = ICONS.chevronDown;

  const days = gridFor(month);
  const weekdays = weekdayLabels(locale);
  const focusTarget = selected ?? anchor ?? today;
  const previousMonth = addMonths(new Date(month.getFullYear(), month.getMonth(), 1), -1);
  const nextMonth = addMonths(new Date(month.getFullYear(), month.getMonth(), 1), 1);

  /*
   * RUTENETTET FØLGER DATOEN SOM BLIR SKREVET (QA-punkt 23).
   *
   * Rapporten: «Den hopper heller ikke til en innskrevet dato (18.06.2027 viste september 2026).»
   *
   * `month` settes ÉN gang når panelet monteres, og en dato som skrives i feltet etterpå endrer
   * `selected` uten at måneden følger med. Da ser man på et rutenett som ikke har noe med tallet
   * i feltet å gjøre.
   *
   * Bare når MÅNEDEN faktisk er en annen: uten den sjekken ville hver eneste tastetrykk satt
   * tilstanden på nytt, og en leser som bladde framover ville blitt dratt tilbake.
   *
   * ==============================================================================================
   * OG AVHENGIGHETSLISTA VAR DEN ANDRE HALVDELEN AV #17b.
   *
   * Kommentaren under listen sa allerede hvorfor `selectedKey` finnes — «et Date-objekt er nytt
   * ved hver render» — og så sto `selected` der ved siden av likevel. Med det objektet i lista
   * kjørte effekten etter HVER render, ikke bare når datoen endret seg, og da dro den `month`
   * tilbake til den valgte datoens måned i samme tikk som pilen hadde flyttet den.
   *
   * Feilen var altså skrevet ned og deretter opphevet på neste linje. Den er borte nå, og testen
   * «bytter måned når pilen trykkes, også når feltet har en dato» er det som holder den borte.
   */
  const selectedKey = selected === null ? null : toDateValue(selected);
  useEffect(() => {
    if (selectedKey === null) return;
    const target = parseDateValue(selectedKey);
    if (target === null) return;
    setMonth((current) =>
      current.getFullYear() === target.getFullYear() && current.getMonth() === target.getMonth()
        ? current
        : target,
    );
  }, [selectedKey]);

  // The grid is re-rendered by the month buttons too, and the cell that carries the tab stop
  // moves with the month — so the DOM focus has to follow it or the tab order jumps to the
  // start of the panel.
  useEffect(() => {
    const active = document.activeElement;
    const focusInGrid = active instanceof Node && gridRef.current?.contains(active) === true;
    if (!focusInGrid) return;
    gridRef.current?.querySelector<HTMLButtonElement>('[data-focus-target="true"]')?.focus();
  }, [month, gridRef]);

  function focusDay(next: Date): void {
    queueMicrotask(() => {
      gridRef.current
        ?.querySelector<HTMLButtonElement>(`[data-day="${toDateValue(next)}"]`)
        ?.focus();
    });
  }

  /** Arrow keys move focus AND the month, because a month boundary is not a wall. */
  function move(event: ReactKeyboardEvent<HTMLDivElement>, next: Date): void {
    event.preventDefault();
    if (next.getMonth() !== month.getMonth() || next.getFullYear() !== month.getFullYear()) {
      setMonth(next);
    }
    focusDay(next);
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onDismiss();
      return;
    }

    const active = document.activeElement;
    const current =
      active instanceof HTMLElement && active.dataset['day'] !== undefined
        ? parseDateValue(active.dataset['day'])
        : null;
    if (current === null) return;

    const weekOffset = (current.getDay() - FIRST_WEEKDAY + WEEK_LENGTH) % WEEK_LENGTH;
    switch (event.key) {
      case 'ArrowLeft':
        return move(event, addDays(current, -1));
      case 'ArrowRight':
        return move(event, addDays(current, 1));
      case 'ArrowUp':
        return move(event, addDays(current, -WEEK_LENGTH));
      case 'ArrowDown':
        return move(event, addDays(current, WEEK_LENGTH));
      case 'Home':
        return move(event, addDays(current, -weekOffset));
      case 'End':
        return move(event, addDays(current, WEEK_LENGTH - 1 - weekOffset));
      case 'PageUp':
        return move(event, addMonths(current, -1));
      case 'PageDown':
        return move(event, addMonths(current, 1));
      default:
        return undefined;
    }
  }

  return (
    <div className="flex w-[248px] flex-col p-1">
      <div className="flex items-center justify-between gap-1 px-1 pb-1">
        <button
          type="button"
          data-testid="date-prev-month"
          aria-label={monthLabel(previousMonth, locale)}
          onClick={() => setMonth(previousMonth)}
          className="hit-target flex size-7 items-center justify-center rounded-full text-muted ring-focus hover:bg-[var(--fill-tonal)] hover:text-default"
        >
          <Previous aria-hidden className="size-3.5" />
        </button>
        {/*
          OVERSKRIFTEN ER EN KNAPP (#17b: «Klikk på måned og år bør åpne en rask velger»).
          Den beholder `aria-live`, så en måned byttet med piltastene fortsatt annonseres uten at
          fokus flyttes — tilstanden leses opp, og kontrollen er den samme teksten.
        */}
        <button
          type="button"
          data-testid="date-month"
          aria-expanded={quick}
          aria-label={`${monthLabel(month, locale)} — ${t('date.chooseMonth')}`}
          onClick={() => setQuick((open) => !open)}
          className={cn(
            'hit-target flex items-center gap-1 rounded-full px-2 py-0.5 ring-focus',
            'text-xs font-semibold text-default hover:bg-[var(--fill-tonal)]',
          )}
        >
          <span aria-live="polite">{monthLabel(month, locale)}</span>
          <Expand aria-hidden className={cn('size-3 text-subtle', quick && 'rotate-180')} />
        </button>
        <button
          type="button"
          data-testid="date-next-month"
          aria-label={monthLabel(nextMonth, locale)}
          onClick={() => setMonth(nextMonth)}
          className="hit-target flex size-7 items-center justify-center rounded-full text-muted ring-focus hover:bg-[var(--fill-tonal)] hover:text-default"
        >
          <Next aria-hidden className="size-3.5" />
        </button>
      </div>

      {quick ? (
        <QuickPicker
          month={month}
          locale={locale}
          onBrowseYear={(year) => setMonth(new Date(year, month.getMonth(), 1))}
          onPickMonth={(next) => {
            setMonth(next);
            setQuick(false);
          }}
        />
      ) : (
        <>
          <div aria-hidden className="grid grid-cols-7 pb-1">
            {weekdays.map((day) => (
              <span key={day} className="text-center text-2xs font-medium text-subtle uppercase">
                {day}
              </span>
            ))}
          </div>

          <div
            role="grid"
            id={gridId}
            aria-label={monthLabel(month, locale)}
            ref={gridRef}
            onKeyDown={onKeyDown}
            // The tab stop is on a CELL, not here — that is what a roving tabindex is. -1 keeps the
            // grid out of the tab order while still making it a legal focus target, which the role
            // requires of it.
            tabIndex={-1}
            className="grid grid-cols-7 gap-px"
          >
            {/*
          THE WEEKS ARE REAL ROWS.
          `role="grid"` requires `row` children that own the `gridcell`s — the forty-two cells
          used to be direct children of the grid, which is a `grid` that owns no rows at all.
          Read back in Chrome: 42 gridcells, 0 rows. Assistive technology walks a grid by row
          ("rad 3, kolonne 5"), and a grid with none of them announces neither, which makes the
          keyboard navigation this component builds unnarratable. `display: contents` keeps the
          seven-column CSS grid exactly as it was — the rows carry the semantics and no layout.
        */}
            {Array.from({ length: WEEKS_SHOWN }, (_, week) => (
              <div key={week} role="row" className="contents">
                {days.slice(week * WEEK_LENGTH, week * WEEK_LENGTH + WEEK_LENGTH).map((day) => {
                  const inMonth = day.getMonth() === month.getMonth();
                  const isSelected = selected !== null && sameDay(day, selected);
                  const isToday = sameDay(day, today);
                  const blocked = min !== null && toDateValue(day) < min;
                  return (
                    <button
                      key={toDateValue(day)}
                      type="button"
                      role="gridcell"
                      disabled={blocked}
                      aria-disabled={blocked || undefined}
                      data-day={toDateValue(day)}
                      data-focus-target={sameDay(day, focusTarget) ? 'true' : undefined}
                      aria-selected={isSelected}
                      aria-label={longDate(day, locale)}
                      tabIndex={sameDay(day, focusTarget) ? 0 : -1}
                      onClick={() => {
                        if (!blocked) onPick(day);
                      }}
                      className={cn(
                        // 32px, not the 44px floor: seven cells at 44 would be a 308px panel that
                        // does not fit under the field. The cells are adjacent with no dead space
                        // between them, which is the same trade `Table` makes for a 36px row.
                        'flex h-8 items-center justify-center rounded-full',
                        'numeric text-sm outline-none ring-focus',
                        'hover:bg-[var(--fill-tonal)] focus-visible:bg-[var(--fill-tonal)]',
                        inMonth ? 'text-default' : 'text-subtle',
                        blocked &&
                          'cursor-not-allowed text-subtle line-through hover:bg-transparent',
                        // Today is an outline, selected is a fill AND a weight: two different facts,
                        // shown two different ways, neither of them by colour alone (DESIGN.md §3).
                        isToday && !isSelected && 'border border-strong',
                        isSelected &&
                          'bg-accent-strong font-semibold text-on-accent hover:bg-accent-strong focus-visible:bg-accent-strong',
                      )}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex items-center justify-between gap-1 px-1 pt-1">
        <button
          type="button"
          onClick={() => onPick(today)}
          className="hit-target rounded-full px-2 py-1 text-2xs font-semibold text-accent-text ring-focus hover:bg-accent-subtle"
        >
          {todayLabel}
        </button>
        {clearable ? (
          <button
            type="button"
            onClick={onClear}
            className="hit-target rounded-full px-2 py-1 text-2xs font-medium text-muted ring-focus hover:bg-[var(--fill-tonal)] hover:text-default"
          >
            {clearLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

interface QuickPickerProps {
  month: Date;
  locale: Locale;
  /** Pilene og årsrutenettet flytter visningen, så overskriften og raden viser samme år. */
  onBrowseYear: (year: number) => void;
  onPickMonth: (month: Date) => void;
}

/** Tolv år i årsrutenettet, tre i bredden som månedene. */
const YEARS_SHOWN = 12;

/** Første år i tolvårsblokken som rommer `year`: blokkene ligger fast, så pilene blar jevnt. */
function yearBlockStart(year: number): number {
  return year - (((year % YEARS_SHOWN) + YEARS_SHOWN) % YEARS_SHOWN);
}

const NAV_BUTTON =
  'hit-target flex size-7 items-center justify-center rounded-full text-muted ring-focus hover:bg-[var(--fill-tonal)] hover:text-default';

const CELL_BUTTON = cn(
  'flex h-8 items-center justify-center rounded-full text-sm outline-none ring-focus',
  'hover:bg-[var(--fill-tonal)] focus-visible:bg-[var(--fill-tonal)]',
);

/**
 * MÅNED OG ÅR PÅ ÉN GANG — #17b, andre halvdel: «Klikk på måned og år bør åpne en rask velger.»
 *
 * Den som planlegger en årgang som ligger ni måneder fram i tid, og som har oppdaget at pilen
 * flytter én måned av gangen, trykker tolv ganger. Denne flaten er svaret: tolv måneder i et
 * rutenett, og et år man flytter med to piler. Ett klikk til hvilken som helst måned i året, to
 * til hvilket som helst år i nærheten.
 *
 * DEN VELGER INGEN DATO. Å trykke «mars» flytter blikket til mars; verdien i feltet står urørt
 * til noen trykker en dag. Det er forskjellen på å bla og å velge, og å blande dem ville gjort
 * hurtigvelgeren til en felle for den som bare lette.
 *
 * DEN ERSTATTER RUTENETTET, den legger seg ikke oppå det (J1: aldri et lag over et lag). Panelet
 * er den samme flaten hele veien, og overskriftsknappen er veien tilbake.
 *
 * ÅRET BOR I `MonthGrid`, IKKE HER (bølge 0, A4). Velgeren hadde sitt eget år: pilene flyttet det,
 * mens overskriften over viste måneden med det gamle året. En fødselsdato 35 år tilbake ble lest
 * som feil år. Nå flytter pilene visningsmåneden, og årstallet er en knapp til et rutenett med
 * tolv år, der pilene hopper tolv.
 *
 * MÅNEDSNAVNENE KOMMER FRA `Intl`, som resten av denne fila: tolv navn i to språk er tjuefire
 * strenger som allerede finnes i hver nettleser.
 */
function QuickPicker({ month, locale, onBrowseYear, onPickMonth }: QuickPickerProps): ReactNode {
  const { t } = useTranslation();
  const year = month.getFullYear();
  /** `null`: månedene. Et tall: årsrutenettet, med blokkens første år. */
  const [yearBlock, setYearBlock] = useState<number | null>(null);
  const Previous = ICONS.chevronLeft;
  const Next = ICONS.chevronRight;

  const names = new Intl.DateTimeFormat(intlTag(locale), { month: 'short' });

  if (yearBlock !== null) {
    const last = yearBlock + YEARS_SHOWN - 1;
    return (
      <div data-testid="date-quick-picker" className="flex flex-col gap-1 pb-1">
        <div className="flex items-center justify-between gap-1 px-1">
          <button
            type="button"
            data-testid="date-quick-years-prev"
            aria-label={`${String(yearBlock - YEARS_SHOWN)}–${String(yearBlock - 1)}`}
            onClick={() => setYearBlock(yearBlock - YEARS_SHOWN)}
            className={NAV_BUTTON}
          >
            <Previous aria-hidden className="size-3.5" />
          </button>
          <p
            data-testid="date-quick-years"
            aria-live="polite"
            className="numeric text-xs font-semibold text-default"
          >
            {`${String(yearBlock)}–${String(last)}`}
          </p>
          <button
            type="button"
            data-testid="date-quick-years-next"
            aria-label={`${String(last + 1)}–${String(last + YEARS_SHOWN)}`}
            onClick={() => setYearBlock(yearBlock + YEARS_SHOWN)}
            className={NAV_BUTTON}
          >
            <Next aria-hidden className="size-3.5" />
          </button>
        </div>

        <div
          role="group"
          aria-label={t('date.chooseYear')}
          className="grid grid-cols-3 gap-px px-1"
        >
          {Array.from({ length: YEARS_SHOWN }, (_, index) => {
            const candidate = yearBlock + index;
            const current = candidate === year;
            return (
              <button
                key={candidate}
                type="button"
                data-quick-year={candidate}
                aria-current={current ? 'true' : undefined}
                onClick={() => {
                  onBrowseYear(candidate);
                  setYearBlock(null);
                }}
                className={cn(
                  CELL_BUTTON,
                  'numeric',
                  current ? 'bg-accent-subtle font-semibold text-accent-text' : 'text-default',
                )}
              >
                {candidate}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="date-quick-picker" className="flex flex-col gap-1 pb-1">
      <div className="flex items-center justify-between gap-1 px-1">
        <button
          type="button"
          data-testid="date-quick-year-prev"
          aria-label={String(year - 1)}
          onClick={() => onBrowseYear(year - 1)}
          className={NAV_BUTTON}
        >
          <Previous aria-hidden className="size-3.5" />
        </button>
        <button
          type="button"
          data-testid="date-quick-year"
          aria-live="polite"
          aria-label={`${String(year)}, ${t('date.chooseYear')}`}
          onClick={() => setYearBlock(yearBlockStart(year))}
          className={cn(
            'hit-target numeric rounded-full px-2 py-0.5 ring-focus',
            'text-xs font-semibold text-default hover:bg-[var(--fill-tonal)]',
          )}
        >
          {year}
        </button>
        <button
          type="button"
          data-testid="date-quick-year-next"
          aria-label={String(year + 1)}
          onClick={() => onBrowseYear(year + 1)}
          className={NAV_BUTTON}
        >
          <Next aria-hidden className="size-3.5" />
        </button>
      </div>

      <div role="group" aria-label={t('date.chooseMonth')} className="grid grid-cols-3 gap-px px-1">
        {Array.from({ length: 12 }, (_, index) => {
          const candidate = new Date(year, index, 1);
          const current = index === month.getMonth();
          return (
            <button
              key={index}
              type="button"
              data-quick-month={index}
              aria-current={current ? 'true' : undefined}
              onClick={() => onPickMonth(candidate)}
              className={cn(
                CELL_BUTTON,
                current ? 'bg-accent-subtle font-semibold text-accent-text' : 'text-default',
              )}
            >
              {names.format(candidate)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
